import { CANVAS_SIZE } from './canvas-utils';

/**
 * Compute the axis-aligned bounding box of a single rotated rectangle layer.
 * Coords are in the same logical (0..CANVAS_SIZE) space.
 */
export function getRotatedBounds(layer) {
  const cx = layer.x + layer.width / 2;
  const cy = layer.y + layer.height / 2;
  const r = (layer.rotation * Math.PI) / 180;
  const cos = Math.cos(r), sin = Math.sin(r);
  const corners = [
    { x: -layer.width / 2, y: -layer.height / 2 },
    { x:  layer.width / 2, y: -layer.height / 2 },
    { x:  layer.width / 2, y:  layer.height / 2 },
    { x: -layer.width / 2, y:  layer.height / 2 },
  ].map((c) => ({
    x: cx + c.x * cos - c.y * sin,
    y: cy + c.x * sin + c.y * cos,
  }));
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

/**
 * Compute the union bounding box of all layers, clamped to the canvas.
 * Returns null when there are no layers (caller should fall back).
 * @param padding extra logical pixels around content
 */
export function getContentBounds(layers, padding = 12) {
  if (!layers || !layers.length) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const l of layers) {
    const b = getRotatedBounds(l);
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
  }
  // pad and clamp to canvas
  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(CANVAS_SIZE, maxX + padding);
  maxY = Math.min(CANVAS_SIZE, maxY + padding);
  return {
    x: minX,
    y: minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

/**
 * Crop a full-canvas dataURL down to `bounds` (logical units relative to CANVAS_SIZE).
 * Returns a new dataURL (PNG or JPG depending on `mime`).
 *  fullDataUrl can be any resolution >= CANVAS_SIZE; we scale `bounds` to match.
 */
export function cropDataUrl(fullDataUrl, bounds, mime = 'image/png', quality = 0.95) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Scale bounds from CANVAS_SIZE-logical space to actual image pixel space.
      const scale = img.width / CANVAS_SIZE;
      const sx = bounds.x * scale;
      const sy = bounds.y * scale;
      const sw = bounds.width * scale;
      const sh = bounds.height * scale;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);
      const ctx = canvas.getContext('2d');
      // For JPG, fill white background to avoid black where source was transparent
      if (mime === 'image/jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL(mime, quality));
    };
    img.onerror = reject;
    img.src = fullDataUrl;
  });
}
